import './index-map.scss';
import Base from '../base/base';
import { debounce } from '../../shared/utils/helpers';
import { BREAKPOINTS } from '../../shared/utils/variables';
import { loadMapboxGl, MapboxGL } from '../../shared/utils/mapbox';

/** Smallest inset worth applying, so even a short map frames its markers */
const MIN_FIT_PADDING = 48;
/** Used when the map element reports no size yet (pre-layout, or jsdom) */
const FALLBACK_FIT_PADDING = 64;
/** A marker pill's height. Anchored bottom, so it rises above its own point. */
const MARKER_HEIGHT = 28;
/** Half-extent the fit must keep, so padding can never consume the container */
const MIN_FIT_CORE = 40;

/** One text line in the popup, read from a single data attribute */
export interface PopupFieldDef {
  /** Item data attribute supplying the text */
  attribute: string;
  /** Class applied to the line, which carries its typography */
  className: string;
  /** Prepended to a non-empty value, e.g. 'Starting at ' */
  prefix?: string;
}

interface IndexMapOptions {
  /** Selector for the map container element */
  selector?: string;
  /** Mapbox access token (required; the map is disabled without it) */
  accessToken?: string;
  /** Selector for the wrapper around the collection (initial item scan) */
  wrapSelector?: string;
  /** Selector for the items to plot, scoped to the wrapper */
  itemSelector?: string;
  /** Items matching this selector are ignored (e.g. Webflow ghost items) */
  excludeSelector?: string;
  /** Item attribute holding latitude */
  latAttribute?: string;
  /** Item attribute holding longitude */
  lngAttribute?: string;
  /** Item attribute holding the display price (e.g. "$390,000") */
  priceAttribute?: string;
  /** Item attribute holding the status (e.g. "Sold") */
  statusAttribute?: string;
  /** Item attribute holding the full address */
  addressAttribute?: string;
  /**
   * Item attribute the marker pill displays. Defaults to priceAttribute, i.e.
   * a price pill. Point it at e.g. 'data-neighborhood' to label markers by
   * name instead.
   */
  labelAttribute?: string;
  /**
   * How to render the marker label. 'price' compacts it ("$390,000" -> "$390k");
   * 'text' shows the attribute as written. Default 'price'.
   */
  labelFormat?: 'price' | 'text';
  /**
   * The popup's text lines, in render order. Each reads one data attribute;
   * `prefix` is prepended only when that attribute has a value, so an empty
   * attribute drops its line rather than leaving a dangling prefix.
   * Defaults to status, price and address — the home-listing shape.
   */
  popupFields?: PopupFieldDef[];
  /** Selector (within an item) for the detail link surfaced in the popup */
  detailLinkSelector?: string;
  /** Statuses that render as a muted marker (e.g. ['Sold']) */
  soldStatuses?: string[];
  /** Mapbox style URL */
  mapStyle?: string;
  /** Fallback center [lng, lat] when there are no items to fit */
  center?: [number, number];
  /** Fallback zoom when there are no items to fit */
  zoom?: number;
  /** Cap on fitBounds zoom, so a tight cluster doesn't over-zoom */
  maxZoom?: number;
  /** Zoom used when a single item is shown */
  singleZoom?: number;
  /**
   * Inset kept between the outermost markers and the container edge when
   * fitting, as a fraction of the dimension it applies to — the sides scale
   * with width, the top and bottom with height. Proportional rather than a
   * fixed pixel count so one value suits a wide desktop column and a phone
   * alike. Raise it to frame the markers more loosely. Default 0.18.
   */
  fitPaddingRatio?: number;
  /**
   * Highlight a card and its marker together on hover, in both directions.
   * Each side reuses the hover state it already has, so the linked look and
   * the moused-over look are the same rule. Default true.
   */
  hoverLink?: boolean;
  /**
   * Selector of a fixed header to stick beneath. When set, the container
   * becomes position: sticky with the header's measured height as its top
   * offset (re-measured via ResizeObserver, so it tracks an animating header).
   */
  stickyUnder?: string;
  debug?: boolean;
  [key: string]: any;
}

interface IndexMapConfig {
  selector: string;
  accessToken: string;
  wrapSelector: string;
  itemSelector: string;
  excludeSelector: string;
  latAttribute: string;
  lngAttribute: string;
  priceAttribute: string;
  statusAttribute: string;
  addressAttribute: string;
  labelAttribute: string;
  labelFormat: 'price' | 'text';
  popupFields: PopupFieldDef[];
  detailLinkSelector: string;
  soldStatuses: string[];
  mapStyle: string;
  center: [number, number];
  zoom: number;
  maxZoom: number;
  singleZoom: number;
  fitPaddingRatio: number;
  hoverLink: boolean;
  stickyUnder: string;
}

/**
 * IndexMap
 *
 * Plots a Webflow CMS collection on a Mapbox map: one pill marker per item
 * (read from the items' lat/lng/status data attributes), with a click popup
 * carrying an image, a configurable set of text lines, and a detail link.
 *
 * What the pill and the popup lines say is configuration, not code:
 * `labelAttribute` and `labelFormat` choose the pill's text, and `popupFields`
 * lists the popup's lines. The defaults produce the home-listing shape — a
 * compacted price pill over a status/price/address popup — so a page that
 * configures neither behaves exactly as before. The neighborhoods page points
 * them at `data-neighborhood` instead. Pair it with
 * IndexFilters via `onChange` — `setItems(matching)` redraws the markers
 * and refits the view to the filtered set.
 *
 * Mapbox GL is loaded lazily from the CDN. On viewports where the container is
 * hidden (≤ BREAKPOINTS.mobile) the map is not created and nothing is fetched
 * until the viewport crosses the breakpoint, so phones never pay for it.
 */
export default class IndexMap extends Base {
  static readonly CONTAINER_CLASS = 'clm';
  static readonly STICKY_CLASS = 'clm--sticky';
  /** Marker highlight, sharing its rule with .clm-marker:hover */
  static readonly MARKER_HOVER_CLASS = 'clm-marker--hover';
  /** Card-link highlight, sharing its rule with the card's own :hover */
  static readonly CARD_HOVER_CLASS = 'is-hovered';

  private config: IndexMapConfig;

  private container: HTMLElement | null = null;
  private mapEl: HTMLElement | null = null;
  private mapboxgl: MapboxGL = null;
  private map: any = null;
  private markers: any[] = [];
  private items: HTMLElement[] = [];
  /** Hover listeners bound per render, released alongside the markers */
  private hoverListeners: { element: HTMLElement; event: string; handler: EventListener }[] = [];
  /** Elements currently carrying a highlight, so a re-render never strands one */
  private highlighted: { element: HTMLElement; className: string }[] = [];

  private mapReady = false;
  private creating = false;
  private destroyed = false;

  private visibleQuery: MediaQueryList | null = null;
  private visibleHandler: (() => void) | null = null;
  private stickyObserver: ResizeObserver | null = null;
  private stickyResize: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(options: IndexMapOptions = {}) {
    super(options.debug || false);

    this.config = {
      selector: '#listings-map',
      accessToken: '',
      wrapSelector: '.home-collection-wrap',
      itemSelector: '.ml-item',
      excludeSelector: '.ml--ghost',
      latAttribute: 'data-lat',
      lngAttribute: 'data-long',
      priceAttribute: 'data-price',
      statusAttribute: 'data-status',
      addressAttribute: 'data-full-address',
      labelAttribute: '',
      labelFormat: 'price',
      popupFields: [],
      detailLinkSelector: 'a',
      soldStatuses: ['Sold'],
      mapStyle: 'mapbox://styles/mapbox/streets-v12',
      center: [-83.0458, 42.3314], // Detroit
      zoom: 11,
      maxZoom: 19,
      singleZoom: 15,
      fitPaddingRatio: 0.18,
      hoverLink: true,
      stickyUnder: '',
      ...options
    };

    // Resolved after the spread so they follow any overridden attribute names
    if (!this.config.labelAttribute) {
      this.config.labelAttribute = this.config.priceAttribute;
    }
    if (!this.config.popupFields.length) {
      this.config.popupFields = [
        { attribute: this.config.statusAttribute, className: 'clm-popup__status' },
        { attribute: this.config.priceAttribute, className: 'clm-popup__price' },
        { attribute: this.config.addressAttribute, className: 'clm-popup__address' }
      ];
    }

    this.init();
  }

  init(): void {
    this.container = document.querySelector<HTMLElement>(this.config.selector);
    if (!this.container) {
      this.log('Map container not found, skipping', this.config.selector);
      return;
    }
    if (!this.config.accessToken) {
      this.log('No Mapbox access token provided, map disabled');
      return;
    }

    this.container.classList.add(IndexMap.CONTAINER_CLASS);

    // Initial set: the full collection. Filter changes arrive via setItems().
    const wrap = document.querySelector<HTMLElement>(this.config.wrapSelector);
    if (wrap) {
      this.items = Array.from(wrap.querySelectorAll<HTMLElement>(this.config.itemSelector)).filter(
        (item) => !this.config.excludeSelector || !item.matches(this.config.excludeSelector)
      );
    }

    this.initSticky();
    this.initLazyInit();

    this.log(`Initialized over ${this.items.length} item(s)`);
  }

  /**
   * Replace the plotted set (e.g. the filter's matching items) and refit.
   * Stores the set even before the map exists, so it renders once created.
   */
  setItems(items: HTMLElement[]): void {
    this.items = items;
    if (this.mapReady) this.renderMarkers();
  }

  /**
   * Make the map present and correctly sized — call this when the container
   * becomes visible (e.g. a mobile List/Map toggle switching into map view).
   * Creates the map on first call, even on mobile where init defers creation
   * so a phone in list view never fetches Mapbox. Resizing on show is required:
   * Mapbox paints a blank/clipped canvas if it was measured while the container
   * was display:none. Safe to call repeatedly.
   */
  activate(): void {
    if (this.destroyed) return;
    this.ensureMap();
    if (this.mapReady) this.map?.resize();
  }

  /* ===================
     Map lifecycle
     =================== */

  /** Create the map now if the container is visible; otherwise wait for it */
  private initLazyInit(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      this.ensureMap();
      return;
    }

    this.visibleQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.mobile + 1}px)`);
    if (this.visibleQuery.matches) {
      this.ensureMap();
      return;
    }

    // Hidden (mobile): defer creation — and the CDN fetch — until shown
    this.visibleHandler = () => {
      if (this.visibleQuery!.matches) this.ensureMap();
    };
    this.visibleQuery.addEventListener('change', this.visibleHandler);
  }

  private async ensureMap(): Promise<void> {
    if (this.map || this.creating || this.destroyed || !this.container) return;
    this.creating = true;

    try {
      const mapboxgl = await loadMapboxGl();
      if (this.destroyed || !this.container || this.map) return;
      this.mapboxgl = mapboxgl;

      // Render Mapbox into a child element: Mapbox forces `position: relative`
      // on its root (.mapboxgl-map), which would otherwise override the
      // container's `position: sticky`. Keeping them separate lets the column
      // stick while Mapbox owns the inner element.
      this.mapEl = document.createElement('div');
      this.mapEl.className = 'clm-canvas';
      this.container.appendChild(this.mapEl);

      mapboxgl.accessToken = this.config.accessToken;
      this.map = new mapboxgl.Map({
        container: this.mapEl,
        style: this.config.mapStyle,
        center: this.config.center,
        zoom: this.config.zoom,
        attributionControl: false
      });
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      this.map.addControl(new mapboxgl.AttributionControl({ compact: true }));

      this.map.on('load', () => {
        if (this.destroyed) return;
        this.mapReady = true;
        this.map.resize();
        this.renderMarkers();
        this.observeContainerResize();
        this.log('Map ready');
      });
    } catch (error) {
      this.log('Mapbox failed to load; container keeps its placeholder', error);
    } finally {
      this.creating = false;
    }
  }

  /* ===================
     Markers
     =================== */

  private renderMarkers(): void {
    if (!this.map || !this.mapboxgl) return;
    this.clearMarkers();

    const seen = new Map<string, number>();
    const bounds = new this.mapboxgl.LngLatBounds();
    let plotted = 0;

    this.items.forEach((item) => {
      const lat = parseFloat(item.getAttribute(this.config.latAttribute) || '');
      const lng = parseFloat(item.getAttribute(this.config.lngAttribute) || '');
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // Held in a variable rather than read back off the marker, so the pairing
      // needs no Mapbox accessor and survives however the marker stores it
      const markerEl = this.buildMarkerEl(item);
      const [olng, olat] = this.spread(lng, lat, seen);
      const marker = new this.mapboxgl.Marker({ element: markerEl, anchor: 'bottom' })
        .setLngLat([olng, olat])
        .setPopup(this.buildPopup(item))
        .addTo(this.map);

      this.markers.push(marker);
      this.linkHover(item, markerEl);
      bounds.extend([olng, olat]);
      plotted += 1;
    });

    this.log(`Plotted ${plotted} of ${this.items.length} item(s)`);
    if (plotted > 0) this.fitToBounds(bounds, plotted);
  }

  private clearMarkers(): void {
    this.unlinkHover();
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
  }

  /* ===================
     Card <-> marker hover
     =================== */

  /**
   * Tie a card and its marker together, each hover lighting the other.
   *
   * Both sides reuse the hover state they already have — the marker's
   * `:hover` rule and the card link's — so a linked highlight and a moused-over
   * one are the same declarations, and changing one changes both.
   *
   * The card side binds to the inner link rather than the item: it is the
   * element the stylesheet targets, and it is equivalent across both pages,
   * where the items themselves are not (`.ml-item` on listings, `.kl-hcard` on
   * neighborhoods).
   */
  private linkHover(item: HTMLElement, markerEl: HTMLElement): void {
    if (!this.config.hoverLink) return;

    // On touch, mouseenter fires on tap while mouseleave may never follow,
    // which would strand a highlight on
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      !window.matchMedia('(hover: hover)').matches
    ) {
      return;
    }

    const link = item.querySelector<HTMLElement>(this.config.detailLinkSelector);
    if (!link) return;

    this.bindHover(markerEl, link, IndexMap.CARD_HOVER_CLASS);
    this.bindHover(link, markerEl, IndexMap.MARKER_HOVER_CLASS);
  }

  /** Hovering `source` toggles `className` on `target` */
  private bindHover(source: HTMLElement, target: HTMLElement, className: string): void {
    const on = () => {
      target.classList.add(className);
      this.highlighted.push({ element: target, className });
    };
    const off = () => target.classList.remove(className);

    source.addEventListener('mouseenter', on);
    source.addEventListener('mouseleave', off);
    this.hoverListeners.push(
      { element: source, event: 'mouseenter', handler: on },
      { element: source, event: 'mouseleave', handler: off }
    );
  }

  /** Release every hover listener and clear any highlight still applied */
  private unlinkHover(): void {
    this.hoverListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this.hoverListeners = [];

    this.highlighted.forEach(({ element, className }) => element.classList.remove(className));
    this.highlighted = [];
  }

  /**
   * Fan out markers that share a coordinate (e.g. A/B duplex units at one
   * address) along a deterministic golden-angle spiral, so each stays
   * clickable instead of stacking invisibly.
   */
  private spread(lng: number, lat: number, seen: Map<string, number>): [number, number] {
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    const index = seen.get(key) || 0;
    seen.set(key, index + 1);
    if (index === 0) return [lng, lat];

    const goldenAngle = 2.399963229728653;
    const radius = 0.00009 * Math.sqrt(index); // ~10m per step
    return [lng + radius * Math.cos(index * goldenAngle), lat + radius * Math.sin(index * goldenAngle)];
  }

  private fitToBounds(bounds: any, count: number): void {
    if (!this.map) return;
    const duration = this.prefersReducedMotion() ? 0 : 600;

    if (count === 1) {
      this.map.easeTo({ center: bounds.getCenter(), zoom: this.config.singleZoom, duration });
      return;
    }
    this.map.fitBounds(bounds, {
      padding: this.fitPadding(),
      maxZoom: this.config.maxZoom,
      duration
    });
  }

  /**
   * Per-side inset for fitBounds, in pixels.
   *
   * Mapbox pads the coordinate bounds, but a marker is a pill anchored at its
   * coordinate, so the pill overhangs that inset — which is why a flat 64px
   * left the outermost markers touching the edge. Each axis is a fraction of
   * its own dimension, so one ratio suits a wide desktop column and a phone,
   * and each is clamped so Mapbox is never handed padding it cannot honour.
   */
  private fitPadding(): { top: number; right: number; bottom: number; left: number } {
    const width = this.mapEl?.clientWidth || 0;
    const height = this.mapEl?.clientHeight || 0;

    const scale = (extent: number): number =>
      extent > 0
        ? Math.max(Math.round(extent * this.config.fitPaddingRatio), MIN_FIT_PADDING)
        : FALLBACK_FIT_PADDING;

    // An unmeasured axis has no container to clamp against
    const cap = (extent: number): number =>
      extent > 0 ? Math.max(0, Math.floor(extent / 2) - MIN_FIT_CORE) : Infinity;

    const side = Math.min(scale(width), cap(width));
    const vertical = scale(height);
    const capV = cap(height);

    return {
      left: side,
      right: side,
      // The northernmost pill rises above its point, so give the top its height
      top: Math.min(vertical + MARKER_HEIGHT, capV),
      bottom: Math.min(vertical, capV)
    };
  }

  private buildMarkerEl(item: HTMLElement): HTMLElement {
    const status = (item.getAttribute(this.config.statusAttribute) || '').trim();
    const muted = this.config.soldStatuses.some((s) => s.toLowerCase() === status.toLowerCase());
    const raw = (item.getAttribute(this.config.labelAttribute) || '').trim();
    const label = this.config.labelFormat === 'text' ? raw : this.compactPrice(raw);
    const address = (item.getAttribute(this.config.addressAttribute) || '').trim();

    const el = document.createElement('button');
    el.type = 'button';
    el.className = muted ? 'clm-marker clm-marker--muted' : 'clm-marker';
    el.textContent = label || '•';
    // Name it by whichever of address and label it actually has: a home has
    // both, a neighborhood has only the label since its address is empty
    el.setAttribute('aria-label', [address, label].filter(Boolean).join(', ') || 'Listing');
    return el;
  }

  private buildPopup(item: HTMLElement): any {
    const href = item.querySelector(this.config.detailLinkSelector)?.getAttribute('href') || '';
    const img = item.querySelector('img.image')?.getAttribute('src') || '';

    const wrap = document.createElement('a');
    wrap.className = 'clm-popup';

    if (img) wrap.appendChild(this.popupImage(img));

    // Lines stack in their own column beside the image, so the field list can
    // be any length and a class carries only typography, never position
    const body = document.createElement('div');
    body.className = 'clm-popup__body';

    this.config.popupFields.forEach((field) => {
      const value = (item.getAttribute(field.attribute) || '').trim();
      // Skip the line entirely rather than render a dangling prefix
      if (!value) return;
      body.appendChild(this.popupLine(field.className, `${field.prefix || ''}${value}`));
    });

    wrap.appendChild(body);

    if (href) {
      wrap.href = href;
    } else {
      this.log('did not find href');
    }

    // setDOMContent (not setHTML): CMS strings stay as text, no injection risk
    return new this.mapboxgl.Popup({ offset: 18, className: 'clm-popup-shell', maxWidth: '300px' }).setDOMContent(wrap);
  }

  private popupImage(imgSrc: string): HTMLElement {
    const el = document.createElement('img');
    el.className = 'clm-popup__image';
    el.src = imgSrc;
    return el;
  }

  private popupLine(className: string, text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = className;
    el.textContent = text;
    return el;
  }

  /** "$390,000" → "$390k"; "$1,250,000" → "$1.3M"; non-numeric → "" */
  private compactPrice(raw: string): string {
    const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n >= 1_000_000) {
      const m = n / 1_000_000;
      return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
    }
    return `$${Math.round(n / 1000)}k`;
  }

  private prefersReducedMotion(): boolean {
    return (
      (typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      false
    );
  }

  /* ===================
     Sticky + resize
     =================== */

  private initSticky(): void {
    if (!this.config.stickyUnder || !this.container) return;

    this.container.classList.add(IndexMap.STICKY_CLASS);
    this.measureStickyOffset();

    // Track the header height directly so the map follows a header that
    // animates its size on scroll (the nav compacting), not just on resize.
    const header = document.querySelector<HTMLElement>(this.config.stickyUnder);
    if (header && typeof ResizeObserver !== 'undefined') {
      this.stickyObserver = new ResizeObserver(() => this.measureStickyOffset());
      this.stickyObserver.observe(header);
    }

    this.stickyResize = debounce(() => this.measureStickyOffset(), 150);
    window.addEventListener('resize', this.stickyResize);
  }

  private measureStickyOffset(): void {
    if (!this.container) return;
    const header = document.querySelector<HTMLElement>(this.config.stickyUnder);
    const offset = header ? header.offsetHeight : 0;
    this.container.style.setProperty('--clm-sticky-top', `${offset}px`);
  }

  private observeContainerResize(): void {
    if (typeof ResizeObserver === 'undefined' || !this.container) return;
    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.container);
  }

  /* ===================
     Cleanup
     =================== */

  destroy(): void {
    this.destroyed = true;

    if (this.visibleQuery && this.visibleHandler) {
      this.visibleQuery.removeEventListener('change', this.visibleHandler);
    }
    this.visibleQuery = null;
    this.visibleHandler = null;

    if (this.stickyResize) {
      window.removeEventListener('resize', this.stickyResize);
      this.stickyResize = null;
    }
    this.stickyObserver?.disconnect();
    this.stickyObserver = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.clearMarkers();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.mapEl?.remove();
    this.mapEl = null;
    this.mapReady = false;

    if (this.container) {
      this.container.classList.remove(IndexMap.CONTAINER_CLASS, IndexMap.STICKY_CLASS);
      this.container.style.removeProperty('--clm-sticky-top');
    }
  }
}
